/**
 * Jeton d'aperçu d'un brouillon CMS (refonte CMS, lot 4).
 *
 * Le bouton « Aperçu » de l'éditeur (supervisor) ouvre `/boutique#apercu=<jeton>`. Le jeton
 * voyage dans le fragment pour ne jamais atteindre le serveur web : on le lit au démarrage de
 * l'app, avant toute redirection de connexion, on le range en `sessionStorage` (cet onglet
 * seulement) et on efface le fragment de l'URL. Les lectures de la page `shop_index` l'envoient
 * ensuite dans l'en-tête `X-Cms-Preview`.
 */

export const CMS_PREVIEW_HASH_PARAM = "apercu";
export const CMS_PREVIEW_STORAGE_KEY = "yeati:cmsPreviewToken";
export const CMS_PREVIEW_HEADER = "X-Cms-Preview";
/** Seule page qui a un emplacement connu dans l'espace salarié. */
export const CMS_PREVIEW_PAGE_CODE = "shop_index";

type Listener = () => void;
const listeners = new Set<Listener>();

const notify = () => listeners.forEach((listener) => listener());

const readStorage = (): string | null => {
  try {
    return window.sessionStorage.getItem(CMS_PREVIEW_STORAGE_KEY);
  }
  catch {
    return null;
  }
};

export const getCmsPreviewToken = (): string | null => readStorage() || null;

export const setCmsPreviewToken = (token: string): void => {
  try {
    window.sessionStorage.setItem(CMS_PREVIEW_STORAGE_KEY, token);
  }
  catch {
    // Stockage indisponible (navigation privée stricte) : pas d'aperçu, la boutique reste en ligne.
  }
  notify();
};

export const clearCmsPreviewToken = (): void => {
  try {
    window.sessionStorage.removeItem(CMS_PREVIEW_STORAGE_KEY);
  }
  catch {
    // Rien à effacer.
  }
  notify();
};

export const subscribeCmsPreviewToken = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  }
  catch {
    return value;
  }
};

/**
 * Extrait le jeton d'un fragment `#apercu=<jeton>`. Le jeton est du base64 brut (`+`, `/`, `=`) :
 * pas de `URLSearchParams`, qui changerait les `+` en espaces.
 */
export const parseCmsPreviewHash = (hash: string): { token: string | null, rest: string } => {
  const parts = hash.replace(/^#/, "").split("&").filter(Boolean);
  const prefix = `${CMS_PREVIEW_HASH_PARAM}=`;
  const match = parts.find((part) => part.startsWith(prefix));
  if (!match) return { token: null, rest: hash };

  const token = safeDecode(match.slice(prefix.length)).trim();
  const restParts = parts.filter((part) => part !== match);
  return { token: token || null, rest: restParts.length ? `#${restParts.join("&")}` : "" };
};

/**
 * À appeler une fois, avant le rendu de l'app : range le jeton du fragment et l'efface de l'URL
 * (`history.replaceState`, sans recharger ni ajouter d'entrée d'historique).
 */
export const captureCmsPreviewTokenFromUrl = (
  location: Pick<Location, "pathname" | "search" | "hash"> = window.location,
  history: Pick<History, "replaceState" | "state"> = window.history,
): string | null => {
  const { token, rest } = parseCmsPreviewHash(location.hash);
  if (!token) return null;

  setCmsPreviewToken(token);
  history.replaceState(history.state, "", `${location.pathname}${location.search}${rest}`);
  return token;
};
